import type { Dispatch, PropsWithChildren, RefObject, SetStateAction } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import IconCross from '~/vendor/cxy/icons/icons/Cross';
import clsx from 'clsx';

import type { ButtonProps } from '../Button';
import { Button } from '../Button';

import styles from './styles/Dialog.module.scss';

export type DialogProps = {
  hasBackdrop?: boolean;
  isFullScreen?: boolean;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  shouldCloseOnClickOutside?: boolean;
  title: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
} & PropsWithChildren;

const Dialog = ({
  children,
  className,
  hasBackdrop = true,
  isFullScreen = false,
  isOpen = false,
  setIsOpen,
  shouldCloseOnClickOutside = true,
  title,
  triggerRef,
  ...props
}: {
  className?: string;
} & DialogProps) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const handleDialogDismiss = useCallback(
    (event: MouseEvent): void => {
      if (!shouldCloseOnClickOutside) {
        return;
      }

      const rect = dialogRef.current?.getBoundingClientRect();

      const { bottom = 0, left = 0, right = 0, top = 0 } = rect || {};

      if (top === 0 && bottom === 0 && left === 0 && right === 0) {
        return;
      }

      const isClickOutsideDialog =
        event.clientY < top || event.clientY > bottom || event.clientX < left || event.clientX > right;

      const isTriggerConnected = triggerRef?.current?.isConnected ?? false;
      const isClickOnTrigger = isTriggerConnected && triggerRef?.current?.contains(event?.target as HTMLElement);
      const isBackdropClick = event.target === dialogRef.current;

      const shouldClose =
        isOpen && (isBackdropClick || (isClickOutsideDialog && !isClickOnTrigger && isTriggerConnected));

      if (shouldClose) {
        setIsOpen(false);
      }
    },
    [isOpen, shouldCloseOnClickOutside, setIsOpen, triggerRef],
  );

  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }

    if (isOpen) {
      dialogRef.current.showModal();
    } else {
      dialogRef.current.close();
    }
  }, [dialogRef, isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    document.addEventListener('click', handleDialogDismiss);

    return () => {
      document.removeEventListener('click', handleDialogDismiss);
    };
  }, [handleDialogDismiss]);

  return (
    <dialog
      aria-label={`${title}-modal`}
      className={clsx(
        styles.dialog,
        { [styles.hasBackdrop]: hasBackdrop },
        { [styles.isFullScreen]: isFullScreen },
        className,
      )}
      data-cxy-name="dialog"
      id="cxy-dialog"
      ref={dialogRef}
      {...props}
    >
      <div className={styles.closeButtonWrapper}>
        <Button
          aria-label={`Close ${title} Modal`}
          className={styles.closeButton}
          data-testid="close-button"
          onClick={() => setIsOpen(false)}
          variant="ghost"
        >
          <IconCross size="md" />
        </Button>
      </div>
      <div className={styles.dialogWrapper}>{children}</div>
    </dialog>
  );
};

const Panel = ({ children, className, ...props }: { className?: string } & PropsWithChildren) => {
  return (
    <section className={clsx(styles.panel, className)} {...props}>
      <div className={styles.panelWrapper}>{children}</div>
    </section>
  );
};

export type TriggerProps = ButtonProps;

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(({ children, ...props }, ref) => {
  return (
    <Button ref={ref} variant="ghost" {...props}>
      {children}
    </Button>
  );
});

Dialog.Panel = Panel;
Dialog.Trigger = Trigger;

export { Dialog };
